import {Component, Input, OnInit, OnDestroy, ChangeDetectorRef} from '@angular/core';
import { ExtendedModule } from '@angular/flex-layout/extended';
import { NgClass } from '@angular/common';
import { Subscription, catchError, debounceTime, distinctUntilChanged, of, tap } from 'rxjs';
import { ApplicationDialogService } from '../application-dialog-component/application-dialog.service';
import { PortalHotToastService } from '../portal-hot-toast-component/hot-toast.service';
import { CronJob, DEFAULT_LIMIT, DEFAULT_OFFSET, RunCronJobFn, GetCronJobsGroupedFn } from '@portal-workspace/grow-shared-library';
import { FlexModule } from '@angular/flex-layout';
import { CustomContentLoaderComponent } from "../custom-content-loader-component/custom-content-loader.component";
import { CustomPaginatorComponent } from '../custom-paginator-component/custom-paginator/custom-paginator.component';
import { MatTableModule } from '@angular/material/table';
import { PageEvent } from '@angular/material/paginator';
import { loadingFor } from '@ngneat/loadoff';
import { DatePipe, AsyncPipe, } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule, MatLabel } from '@angular/material/form-field';
import { FormBuilder, FormControl, ReactiveFormsModule } from '@angular/forms';
import moment from 'moment';
import { ApplicationTypeIconComponent } from '../application-type-icon/application-type-icon.component';
import { MatOptionModule } from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';
import { MatSortModule } from '@angular/material/sort'; 
import { MatIconModule } from '@angular/material/icon';
import { animate, state, style, transition } from '@angular/animations';
import { trigger } from '@angular/animations';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatDividerModule } from '@angular/material/divider';
import { MatCardModule } from '@angular/material/card';
@Component({
    selector: 'cron-job',
    templateUrl: './cron-job.component.html',
    styleUrls: ['./cron-job.component.scss'],
    standalone: true,
    animations: [
      trigger('detailExpand', [
        state('collapsed', style({ height: '0px', minHeight: '0' })),
        state('expanded', style({ height: '*' })),
        transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
      ]),
    ],
    imports: [
      MatTableModule, 
      NgClass, 
      ExtendedModule, 
      FlexModule,
      CustomContentLoaderComponent,
      CustomPaginatorComponent,
      MatSortModule,
      DatePipe,
      AsyncPipe,
      MatButtonModule,
      MatTooltipModule,
      MatInputModule,
      MatFormFieldModule,
      ReactiveFormsModule,
      ApplicationTypeIconComponent,
      MatOptionModule,
      MatSelectModule,
      MatLabel,
      MatIconModule,
      MatExpansionModule,
      MatDividerModule,
      MatCardModule
    ]
})
export class CronJobsComponent implements OnInit, OnDestroy {
   
   
  @Input({ required: true }) runCronJobFn!: RunCronJobFn;
  @Input({ required: true }) getCronJobsGroupedFn!: GetCronJobsGroupedFn;

  expandedElementId: number | null = null;
  subscriptions: Subscription[] = [];
  moment = moment;
  formControlSearch!: FormControl<string | null>;

  total = 0;
  limit = DEFAULT_LIMIT;
  offset = DEFAULT_OFFSET;
  filter = '';
  loader = loadingFor('tableLoading');
  sorts: { prop: string, dir: 'asc' | 'desc' } | null = null;
  dataSource: CronJob[] = [];
  groupedDataSource: { [category: string]: CronJob[] } = {};
  columnsToDisplay = ['name', 'route', 'createdTime','actions'];
  categories: string[] = [];
  expandedElementIds: { [category: string]: number | null } = {};

  constructor(
    private formBuilder: FormBuilder,
    private toastService: PortalHotToastService,
    private dialogService: ApplicationDialogService,
    private cdr: ChangeDetectorRef
  ) {
    this.formControlSearch = this.formBuilder.control(null);
     
  }
  private resetPagination() {
    this.offset = DEFAULT_OFFSET;
  }

  ngOnInit(): void {
    // Reset expanded state when component initializes
    this.expandedElementId = null;
    this.reload();

    this.subscriptions.push(
      this.formControlSearch.valueChanges.pipe(
        debounceTime(1000),
        distinctUntilChanged(),
        tap(r => {
          if (r) {
            this.filter = r;
            this.offset = DEFAULT_OFFSET;
            this.limit = DEFAULT_LIMIT;
            this.reload();
          } else {
            this.filter = '';
            this.offset = DEFAULT_OFFSET;
            this.limit = DEFAULT_LIMIT;
            this.reload();
          }
        })
      ).subscribe()
    )
  }

  ngOnDestroy(): void {
    // Clean up subscriptions and reset state
    this.subscriptions.forEach(sub => sub.unsubscribe());
    this.subscriptions = [];
    this.expandedElementId = null;
  }

    reload() {
    // Explicitly reset all expanded states
    this.expandedElementId = null;
    
    this.subscriptions.push(
      this.getCronJobsGroupedFn(this.filter).pipe(
        this.loader.tableLoading.track()
      ).subscribe(response => {
        console.log('cronJobs grouped', response);
        this.groupedDataSource = response?.payload || {};
        this.categories = response?.categories || [];
        this.total = response?.total || 0;
        
        // Ensure no rows are expanded after data load
        setTimeout(() => {
          if (!this.filter) {
            this.expandedElementId = null;
            this.expandedElementIds = {};
          }
          this.cdr.detectChanges();
        }, 0);
      })
    );

  }

  
  initPagination() {
    this.total = this.dataSource.length;
    this.limit = DEFAULT_LIMIT;
    this.offset = DEFAULT_OFFSET;
  }

  private reset() {
    this.limit = DEFAULT_LIMIT;
    this.offset = 0;
    this.expandedElementId = null;
  }

 
  onPagination($event: PageEvent) {
    this.limit = $event.pageSize;
    this.offset = $event.pageIndex;
  }

  runCronJob(route: string) {
    console.log('runCronJob', route);
    this.runCronJobFn(route).pipe(
      this.toastService.spinnerObservable(),
      this.toastService.snackBarObservable(`Cron job ${route} run successfully`),
      catchError(error => {
        // Handle the error globally here or show custom toast
        console.error('Cron job error:', error);
        this.toastService.snackBarObservable(`Failed to run cron job ${route}: ${error.message || error}`);
        this.reload();
        return of(null); // Continue stream with null or empty fallback
      })
    )
    .subscribe({
      next: (response) => {
        console.log('response', response);
        this.reload();
      },
      error: (err) => {
        // This block is optional since catchError handles errors,
        // but can be used to log or fallback if catchError emits error again.
        console.error('Subscription error:', err);
      }
    });
  }
   

 
  getCategorizedJobs(): string[] {
    return Object.keys(this.groupedDataSource);
  }

  getJobsForCategory(category: string): CronJob[] {
    return this.groupedDataSource[category] || [];
  }

  onRowClick(element: CronJob, category: string) {
    console.log('onRowClick', element, category);
    console.log('expandedElementIds before:', this.expandedElementIds[category]);
    this.expandedElementIds[category] = this.expandedElementIds[category] === element.id ? null : element.id;
    console.log('expandedElementIds after:', this.expandedElementIds);
    this.cdr.detectChanges();
  }

  forceResetExpandedState() {
    this.expandedElementId = null;
    this.expandedElementIds = {};
    this.cdr.detectChanges();
  }

  trackByFn(index: number, item: CronJob): number {
    return item.id;
  }

  // Helper method for debugging expansion state
  isRowExpanded(rowId: number, category: string): boolean {
    const isExpanded = !!(this.expandedElementIds && this.expandedElementIds[category] === rowId);
    return isExpanded;
  }


  

}
